import { createContext, useContext, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API, CONTACT_INTERESTS } from '../content/data';
import { EASE } from './motion';

const LeadContext = createContext({ open: () => {} });
export const useLead = () => useContext(LeadContext);

export function LeadProvider({ children }) {
  const [state, setState] = useState({ open: false, interest: 'Student Programmes' });
  const open = (interest = 'Student Programmes') => setState({ open: true, interest });
  const close = () => setState((s) => ({ ...s, open: false }));
  return (
    <LeadContext.Provider value={{ open }}>
      {children}
      <LeadModal state={state} close={close} />
    </LeadContext.Provider>
  );
}

function LeadModal({ state, close }) {
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', interest: state.interest });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      await axios.post(`${API}/leads`, { ...form, interest: form.interest || state.interest });
      setStatus('done');
    } catch (err) {
      setStatus('idle');
      setError('Something went wrong. Please try again or email us directly.');
    }
  };

  const inputCls =
    'w-full rounded-xl border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy outline-none transition-colors focus:border-electric placeholder:text-navy/40';

  return (
    <AnimatePresence>
      {state.open && (
        <motion.div
          data-testid="lead-modal"
          className="fixed inset-0 z-[90] flex items-end justify-center md:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-navy/70 backdrop-blur-sm" onClick={close} data-testid="lead-modal-backdrop" />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.45, ease: EASE }}
            className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-paper p-6 shadow-2xl sm:p-8 md:m-6 md:max-w-md md:rounded-3xl"
          >
            <button
              data-testid="lead-modal-close"
              onClick={close}
              className="absolute right-4 top-4 flex touch-target items-center justify-center rounded-full text-navy/50 transition-colors hover:text-navy"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            {status === 'done' ? (
              <div data-testid="lead-success" className="py-10 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-electric" />
                <h3 className="text-h3 mt-5 font-semibold text-navy">Thank you.</h3>
                <p className="mt-2 text-sm leading-relaxed text-navy/70">
                  Your details have reached the DITEC team. We will get back to you shortly.
                </p>
                <button
                  data-testid="lead-success-close"
                  onClick={close}
                  className="mt-6 rounded-full bg-navy px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-electric"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <span className="overline text-electric">Start the Conversation</span>
                <h3 className="text-h3 mt-3 font-semibold text-navy">Tell us where you want to begin.</h3>
                <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid="lead-form">
                  <input data-testid="lead-name-input" required placeholder="Full Name" value={form.name} onChange={set('name')} className={inputCls} />
                  <input data-testid="lead-company-input" placeholder="Company / Organisation (optional)" value={form.company} onChange={set('company')} className={inputCls} />
                  <input data-testid="lead-phone-input" required type="tel" placeholder="Phone Number" value={form.phone} onChange={set('phone')} className={inputCls} />
                  <input data-testid="lead-email-input" required type="email" placeholder="Email Address" value={form.email} onChange={set('email')} className={inputCls} />
                  <select
                    data-testid="lead-interest-select"
                    value={form.interest || state.interest}
                    onChange={set('interest')}
                    className={inputCls}
                  >
                    {CONTACT_INTERESTS.map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                  {error && <p data-testid="lead-error" className="text-xs text-red-600">{error}</p>}
                  <button
                    data-testid="lead-submit-button"
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full touch-target rounded-full bg-electric py-4 text-sm font-semibold text-white transition-colors hover:bg-navy disabled:opacity-60"
                  >
                    {status === 'loading' ? 'Sending…' : 'Submit Enquiry'}
                  </button>
                  <p className="text-center text-xs text-navy/50">
                    Prefer email? <a href="mailto:ditec.org@gmail.com" className="font-semibold text-electric">ditec.org@gmail.com</a>
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
